import { Action } from 'redux'

import ClientNotificationInterface from '../../interfaces/client/Notification'
import ReferenceCollectionInterface from '../../interfaces/client/ReferenceCollection'

import { ADD_NOTIFICATION } from '../../actions/notifications/addNotification'
import { ADD_NOTIFICATIONS } from '../../actions/notifications/addNotifications'
import { REMOVE_NOTIFICATION } from '../../actions/notifications/removeNotification'

import normalizeData from '../../utils/normalizeData'
import filterDuplicateNotifications from '../../utils/notifications/filterDuplicateNotifications'
import addPassengerIdReference from '../../utils/passengers/addPassengerIdReference'

export interface NotificationAction extends Action {
  notification: ClientNotificationInterface
  notifications: ClientNotificationInterface[]
  notificationId: string
}

export interface NotificationsState {
  byId: {
    [id: string]: ClientNotificationInterface
  }
  allIds: string[]
  passengerIdsToNotificationIds: ReferenceCollectionInterface
}

export const initialState: NotificationsState = {
  byId: {},
  allIds: [],
  passengerIdsToNotificationIds: {},
}

const getAllNotifications = (
  state: NotificationsState,
  notifications: ClientNotificationInterface[]
) => {
  const currentNotifications = state.allIds.map((id: string) => state.byId[id])
  const currentNotificationWithoutDuplicates = filterDuplicateNotifications(
    currentNotifications,
    notifications
  )

  return [...currentNotificationWithoutDuplicates, ...notifications]
}

export default (
  state = initialState,
  action: NotificationAction = {
    type: '',
    notification: {
      id: '',
      type: '',
      message: '',
      code: '',
      isGlobal: false,
    },
    notifications: [],
    notificationId: '',
  }
) => {
  switch (action.type) {
    case ADD_NOTIFICATION:
      const allNotificationsIncludingNewNotification = getAllNotifications(
        state,
        [action.notification]
      )

      return {
        ...state,
        byId: normalizeData(allNotificationsIncludingNewNotification),
        allIds: allNotificationsIncludingNewNotification.map(
          (notification: ClientNotificationInterface) => notification.id
        ),
        passengerIdsToNotificationIds:
          action.notification.passengerId && action.notification.id
            ? addPassengerIdReference(state.passengerIdsToNotificationIds, {
                [action.notification.passengerId]: action.notification.id,
              })
            : state.passengerIdsToNotificationIds,
      }
    case ADD_NOTIFICATIONS:
      const allNotificationsIncludingNewNotifications = getAllNotifications(
        state,
        action.notifications
      )

      return {
        ...state,
        byId: normalizeData(allNotificationsIncludingNewNotifications),
        allIds: allNotificationsIncludingNewNotifications.map(
          (notification: ClientNotificationInterface) => notification.id
        ),
        passengerIdsToNotificationIds: action.notifications.reduce(
          (
            allNotificationRefences: ReferenceCollectionInterface,
            notification: ClientNotificationInterface
          ) => {
            if (!notification.passengerId || !notification.id) {
              return allNotificationRefences
            }

            return addPassengerIdReference(allNotificationRefences, {
              [notification.passengerId]: notification.id,
            })
          },
          {}
        ),
      }
    case REMOVE_NOTIFICATION:
      const allCurrentNotitifications = getAllNotifications(state, [])
      const allNewNotifications = allCurrentNotitifications.filter(
        (notification: ClientNotificationInterface) =>
          notification.id !== action.notificationId
      )

      return {
        ...state,
        byId: normalizeData(allNewNotifications),
        allIds: allNewNotifications.map(
          (notification: ClientNotificationInterface) => notification.id
        ),
        passengerIdsToNotificationIds: Object.keys(
          state.passengerIdsToNotificationIds
        ).reduce(
          (
            allNotificationReferences: ReferenceCollectionInterface,
            currentReferenceKey: string
          ) => {
            const currentReferenceValue =
              state.passengerIdsToNotificationIds[currentReferenceKey]

            if (!currentReferenceValue.includes(action.notificationId)) {
              return {
                ...allNotificationReferences,
                [currentReferenceKey]: currentReferenceValue,
              }
            }

            const filteredCurrentReferenceValue = currentReferenceValue.filter(
              (referenceValue: string) =>
                referenceValue !== action.notificationId
            )

            if (filteredCurrentReferenceValue.length) {
              return {
                ...allNotificationReferences,
                [currentReferenceKey]: filteredCurrentReferenceValue,
              }
            }

            return allNotificationReferences
          },
          {}
        ),
      }

    case 'RESET_NOTIFICATIONS':
    case '@@router/LOCATION_CHANGE':
      return initialState

    default:
      return state
  }
}
